import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { SessionUser } from "../../shared/auth/access";
import type { SessionRow } from "./profile-types";

export function useProfileSessionsWorkspace({ user, notify }: { user: SessionUser; notify: (message: string) => void }) {
  const scopeKey = `${user.id}:${user.role}`;
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [revoking, setRevoking] = useState<string[]>([]);
  const currentScope = useRef(scopeKey);
  const loadedScope = useRef(scopeKey);
  const request = useRef(0);
  const inFlight = useRef(new Set<string>());
  const mounted = useRef(true);
  const notifyRef = useRef(notify);
  const generation = useRef(0);
  if (currentScope.current !== scopeKey) generation.current++;
  currentScope.current = scopeKey;
  notifyRef.current = notify;
  const loadSessions = useCallback(async () => {
    const sequence = ++request.current;
    const requestedScope = currentScope.current;
    try {
      const result = await apiRequest<SessionRow[]>("/api/sessions");
      if (sequence !== request.current || requestedScope !== currentScope.current) return;
      loadedScope.current = requestedScope;
      setSessions(result);
    } catch (error) {
      if (sequence === request.current && requestedScope === currentScope.current) notifyRef.current(error instanceof Error ? error.message : "Oturumlar alınamadı.");
    }
  }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    void loadSessions();
    return () => { request.current++; };
  }, [loadSessions, scopeKey]);
  const revoke = async (session: SessionRow) => {
    if (scopeKey !== currentScope.current || !mounted.current) return;
    if (session.current) { notifyRef.current("Geçerli oturumu sağ alttaki çıkış düğmesinden kapatabilirsiniz."); return; }
    if (inFlight.current.has(session.id)) return;
    inFlight.current.add(session.id);
    setRevoking(previous => [...previous, session.id]);
    const requestedGeneration = generation.current;
    const isCurrent = () => mounted.current && currentScope.current === scopeKey && generation.current === requestedGeneration;
    try {
      await apiRequest<void>(`/api/sessions/${session.id}`, { method: "DELETE" });
      if (!isCurrent()) return;
      request.current++;
      setSessions(previous => previous.filter(item => item.id !== session.id));
      notifyRef.current("Seçilen uygulama oturumu sonlandırıldı.");
    } catch (error) {
      if (isCurrent()) notifyRef.current(error instanceof Error ? error.message : "Oturum sonlandırılamadı.");
    } finally {
      inFlight.current.delete(session.id);
      if (mounted.current) setRevoking(previous => previous.filter(id => id !== session.id));
    }
  };
  return { sessions: loadedScope.current === scopeKey ? sessions : [], loadSessions, revoke, revoking };
}
